import React, { useState } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Select,
  InputNumber,
  Typography,
  Tag,
  message,
  Row,
  Col,
  Divider,
  Tooltip,
  Popconfirm,
  Badge,
  Alert,
  Descriptions
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CalculatorOutlined,
  PercentageOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { formatCurrency } from '../utils/formatters';

const { Title, Text } = Typography;
const { Option } = Select;

// Mock data for currencies - replace with real API call
const CURRENCIES = [
  { code: 'USD', name: 'US Dollar', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'British Pound', symbol: '£' },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦' },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
];

// Mock data for FX spreads - replace with real API call
const MOCK_FX_SPREADS = {
  USD: [
    { id: '1', min_amount: 0, max_amount: 1000, fee_percentage: 2.5, currency_code: 'USD' },
    { id: '2', min_amount: 1001, max_amount: 5000, fee_percentage: 2.0, currency_code: 'USD' },
    { id: '3', min_amount: 5001, max_amount: 10000, fee_percentage: 1.5, currency_code: 'USD' },
    { id: '4', min_amount: 10001, max_amount: null, fee_percentage: 1.0, currency_code: 'USD' },
  ],
  EUR: [
    { id: '5', min_amount: 0, max_amount: 1000, fee_percentage: 2.8, currency_code: 'EUR' },
    { id: '6', min_amount: 1001, max_amount: 5000, fee_percentage: 2.3, currency_code: 'EUR' },
    { id: '7', min_amount: 5001, max_amount: null, fee_percentage: 1.8, currency_code: 'EUR' },
  ],
  NGN: [
    { id: '8', min_amount: 0, max_amount: 500000, fee_percentage: 3.0, currency_code: 'NGN' },
    { id: '9', min_amount: 500001, max_amount: 2000000, fee_percentage: 2.5, currency_code: 'NGN' },
    { id: '10', min_amount: 2000001, max_amount: null, fee_percentage: 2.0, currency_code: 'NGN' },
  ]
};

function FXSpreadManagement() {
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSpread, setEditingSpread] = useState(null);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [previewAmount, setPreviewAmount] = useState(1000);
  const [form] = Form.useForm();
  
  // Get current spreads for selected currency
  const currentSpreads = MOCK_FX_SPREADS[selectedCurrency] || [];

  const handleOpenModal = (spread = null) => {
    setEditingSpread(spread);
    if (spread) {
      form.setFieldsValue({
        min_amount: spread.min_amount,
        max_amount: spread.max_amount,
        fee_percentage: spread.fee_percentage,
      });
    } else {
      form.resetFields();
    }
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingSpread(null);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      message.success(`FX spread ${editingSpread ? 'updated' : 'created'} successfully`);
      handleCloseModal();
    } catch (error) {
      message.error(`Failed to ${editingSpread ? 'update' : 'create'} FX spread`);
    }
  };

  const handleDelete = async (id) => {
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      message.success('FX spread deleted successfully');
    } catch (error) {
      message.error('Failed to delete FX spread');
    }
  };

  const formatAmountRange = (min, max) => {
    if (max === null) {
      return `${formatCurrency(min, selectedCurrency)}+`;
    }
    return `${formatCurrency(min, selectedCurrency)} - ${formatCurrency(max, selectedCurrency)}`;
  };

  const calculateSpreadAmount = (amount) => {
    const spread = currentSpreads.find(s => 
      amount >= s.min_amount && (s.max_amount === null || amount <= s.max_amount)
    );
    
    if (!spread) return { fee_amount: 0, fee_percentage: 0, total_amount: amount };
    
    const fee_amount = (amount * spread.fee_percentage) / 100;
    return {
      fee_amount,
      fee_percentage: spread.fee_percentage,
      total_amount: amount + fee_amount
    };
  };

  const columns = [
    {
      title: 'Amount Range',
      dataIndex: 'min_amount',
      key: 'amount_range',
      render: (_, record) => (
        <Text strong>{formatAmountRange(record.min_amount, record.max_amount)}</Text>
      ),
    },
    {
      title: 'Fee Percentage',
      dataIndex: 'fee_percentage',
      key: 'fee_percentage',
      render: (percentage) => (
        <Tag color="blue" icon={<PercentageOutlined />}>
          {percentage}%
        </Tag>
      ),
    },
    {
      title: 'Example (₦100,000)',
      key: 'example',
      render: (_, record) => {
        const example = 100000;
        const { fee_amount, total_amount } = calculateSpreadAmount(example);
        return (
          <Space direction="vertical" size={0}>
            <Text type="secondary">Base: {formatCurrency(example, selectedCurrency)}</Text>
            <Text type="secondary">Fee: {formatCurrency(fee_amount, selectedCurrency)}</Text>
            <Text strong>Total: {formatCurrency(total_amount, selectedCurrency)}</Text>
          </Space>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit spread">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleOpenModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete FX Spread"
            description="Are you sure you want to delete this FX spread? This action cannot be undone."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Tooltip title="Delete spread">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const selectedCurrencyData = CURRENCIES.find(c => c.code === selectedCurrency);
  const { fee_amount, fee_percentage, total_amount } = calculateSpreadAmount(previewAmount);

  return (
    <div style={{ background: '#fff', padding: '24px' }}>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Card bordered={false}>
            <Row gutter={[16, 16]} align="middle" justify="space-between">
              <Col>
                <Title level={4} style={{ margin: 0 }}>
                  FX Spread Management
                </Title>
                <Text type="secondary">
                  Manage fee percentages on amount ranges per currency
                </Text>
              </Col>
              <Col>
                <Space>
                  <Button
                    icon={<CalculatorOutlined />}
                    onClick={() => setPreviewModalVisible(true)}
                  >
                    Preview Calculator
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => handleOpenModal()}
                  >
                    Add Spread Band
                  </Button>
                </Space>
              </Col>
            </Row>

            <Divider />

            <Row gutter={[16, 16]} align="middle" style={{ marginBottom: 24 }}>
              <Col>
                <Text strong>Currency:</Text>
              </Col>
              <Col>
                <Select
                  value={selectedCurrency}
                  onChange={setSelectedCurrency}
                  style={{ width: 200 }}
                >
                  {CURRENCIES.map(currency => (
                    <Option key={currency.code} value={currency.code}>
                      <Space>
                        <span>{currency.symbol}</span>
                        <span>{currency.code}</span>
                        <span style={{ color: '#999' }}>({currency.name})</span>
                      </Space>
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col>
                <Badge 
                  count={currentSpreads.length} 
                  showZero 
                  style={{ backgroundColor: '#52c41a' }}
                >
                  <Tag color="green">Active Bands</Tag>
                </Badge>
              </Col>
            </Row>

            <Alert
              message="FX Spread Information"
              description="FX spreads are fee percentages applied to different amount ranges. These fees are added to the base exchange amount and presented as the total to be debited to customers at checkout."
              type="info"
              showIcon
              icon={<InfoCircleOutlined />}
              style={{ marginBottom: 24 }}
            />

            <Table
              columns={columns}
              dataSource={currentSpreads}
              rowKey="id"
              pagination={false}
              locale={{
                emptyText: (
                  <div style={{ padding: '40px 0', textAlign: 'center' }}>
                    <Text type="secondary">No FX spreads configured for {selectedCurrency}</Text>
                    <br />
                    <Button 
                      type="primary" 
                      icon={<PlusOutlined />}
                      onClick={() => handleOpenModal()}
                      style={{ marginTop: 16 }}
                    >
                      Add First Spread Band
                    </Button>
                  </div>
                )
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Add/Edit Modal */}
      <Modal
        title={editingSpread ? 'Edit FX Spread' : 'Add FX Spread'}
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Minimum Amount"
                name="min_amount"
                rules={[{ required: true, message: 'Minimum amount is required' }]}
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder="0"
                  formatter={value => `${selectedCurrencyData?.symbol}${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Maximum Amount"
                name="max_amount"
                rules={[{ required: true, message: 'Maximum amount is required' }]}
              >
                <InputNumber
                  min={0}
                  style={{ width: '100%' }}
                  placeholder="Leave empty for unlimited"
                  formatter={value => value ? `${selectedCurrencyData?.symbol}${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : ''}
                  parser={value => value.replace(/\$\s?|(,*)/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item
            label="Fee Percentage"
            name="fee_percentage"
            rules={[{ required: true, message: 'Fee percentage is required' }]}
          >
            <InputNumber
              min={0}
              max={100}
              step={0.1}
              style={{ width: '100%' }}
              placeholder="2.5"
              addonAfter="%"
            />
          </Form.Item>

          <Form.Item>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
              >
                {editingSpread ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Preview Calculator Modal */}
      <Modal
        title="FX Spread Calculator"
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={null}
        width={500}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>Test Amount:</Text>
            <InputNumber
              value={previewAmount}
              onChange={setPreviewAmount}
              min={0}
              style={{ width: '100%', marginTop: 8 }}
              formatter={value => `${selectedCurrencyData?.symbol}${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
              parser={value => value.replace(/\$\s?|(,*)/g, '')}
            />
          </div>

          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Base Amount">
              {formatCurrency(previewAmount, selectedCurrency)}
            </Descriptions.Item>
            <Descriptions.Item label="Fee Percentage">
              <Tag color="blue">{fee_percentage}%</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Fee Amount">
              {formatCurrency(fee_amount, selectedCurrency)}
            </Descriptions.Item>
            <Descriptions.Item label="Total Amount">
              <Text strong style={{ color: '#52c41a' }}>
                {formatCurrency(total_amount, selectedCurrency)}
              </Text>
            </Descriptions.Item>
          </Descriptions>

          <Alert
            message="Customer Impact"
            description={`For a ${formatCurrency(previewAmount, selectedCurrency)} transaction, the customer will be charged ${formatCurrency(total_amount, selectedCurrency)} (including ${formatCurrency(fee_amount, selectedCurrency)} in fees).`}
            type="info"
            showIcon
          />
        </Space>
      </Modal>
    </div>
  );
}

export default FXSpreadManagement;
